import { useEffect, useRef, useState } from 'react';
import { comprobarReverso } from './api.js';
import { mensajeDelFallo, useCamara } from './camara.js';
import { useCapturaAutomatica } from './deteccion.js';
import { Boton, BotonSecundario, Firma, Marco, Pantalla, Pasos, Pastilla } from './ui.js';

/**
 * Paso 1 de 3: el DNI, por sus dos caras.
 *
 * ── POR QUÉ HAY QUE CAPTURAR EL REVERSO, Y v1 NO LO HACE ───────────────────────
 *
 * El anverso trae la foto y los nombres impresos. **El reverso trae el MRZ** —esas tres líneas
 * de `<<<`— y el MRZ trae dígitos verificadores: el propio documento dice si sus datos son
 * coherentes, sin preguntarle a nadie. Ver `documents/mrz.ts`.
 *
 * ── LO QUE LAS PRUEBAS REALES ENSEÑARON (18/07/2026) ───────────────────────────
 *
 * Tres rondas con un DNI de verdad, tres lecciones:
 *
 *   · El lazo era de un solo disparo: el reverso no capturaba jamás. → la cara viaja como
 *     `clave` del lazo.
 *   · El reverso disparaba antes de voltear: capturaba el anverso otra vez. → el anverso
 *     EXIGE la foto impresa y el reverso la PROHÍBE.
 *   · El reverso disparó sobre un teclado: sin cara que exigir, la calidad sola no sabe qué
 *     es un reverso. → **el SERVIDOR lo comprueba en el momento** (`comprobarReverso`: OCR
 *     real, ¿hay MRZ o número?). Ilegible → se descarta y se pide de nuevo. Al tercer
 *     intento se acepta y el veredicto final decide: dos personas honestas con mala luz no
 *     se quedan atrapadas en un bucle por nuestro umbral.
 *
 * ── LA COMPROBACIÓN CORRE A LA VISTA, NO EN UNA CÁMARA CONGELADA ───────────────
 *
 * Al capturar el reverso, la persona pasa AL INSTANTE a la pantalla de éxito con sus dos
 * miniaturas, y `comprobarReverso` corre delante de ella: un texto lo dice y Continuar se
 * deshabilita mientras dura. Antes esperaba en la pantalla de cámara, quieta, con una pista
 * que cambiaba — una cámara que no reacciona parece rota. Ver sus fotos ya capturadas es la
 * señal de que todo va bien; si el servidor dice ilegible, se vuelve a la cámara con el aviso.
 */

export interface Capturas {
  anverso: string;
  reverso: string;
}

type Cara = 'anverso' | 'reverso';

/** Cuántos reversos ilegibles se rechazan antes de aceptar y dejar decidir al veredicto. */
const RECHAZOS_MAXIMOS_DE_REVERSO = 2;

export function Escaneo({
  token,
  onListo,
  onVolver,
}: {
  token: string;
  onListo: (c: Capturas) => void;
  onVolver?: () => void;
}) {
  const [cara, setCara] = useState<Cara>('anverso');
  const [anverso, setAnverso] = useState<string | null>(null);
  const [reverso, setReverso] = useState<string | null>(null);
  const [revisando, setRevisando] = useState(false);
  const [comprobando, setComprobando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const rechazosDeReverso = useRef(0);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const marcoRef = useRef<HTMLDivElement | null>(null);
  const [video, setVideo] = useState<HTMLVideoElement | null>(null);
  const { stream, fallo, reintentar } = useCamara('documento');

  useEffect(() => {
    const v = videoRef.current;
    if (v !== null && stream !== null) {
      v.srcObject = stream;
      void v.play().catch(() => undefined);
      setVideo(v);
    }
  }, [stream, revisando]);

  const captura = useCapturaAutomatica(
    video,
    'documento',
    !revisando && !comprobando,
    (jpeg) => {
      if (cara === 'anverso') {
        setAnverso(jpeg);
        // Una pausa antes de pedir el reverso: deja que la persona vea el check del anverso.
        setTimeout(() => setCara('reverso'), 700);
      } else {
        // El reverso no se acepta por las buenas: el servidor confirma que lo es (MRZ o
        // número legible por OCR). Ver la cabecera — la tercera prueba capturó un teclado.
        //
        // Pero la espera NO es en la cámara: se pasa YA a la pantalla de éxito con las dos
        // miniaturas, y la comprobación corre delante de la persona (ver la cabecera). Solo
        // si el servidor dice ilegible se deshace el éxito y se vuelve a la cámara.
        setReverso(jpeg);
        setRevisando(true);
        setComprobando(true);
        setAviso(null);
        comprobarReverso(token, jpeg)
          .then((r) => r.legible)
          // Red o sesión caídas NO atrapan a la persona: se acepta y el veredicto final
          // (que analiza todo de nuevo) tiene la última palabra.
          .catch(() => true)
          .then((legible) => {
            if (legible || rechazosDeReverso.current >= RECHAZOS_MAXIMOS_DE_REVERSO) {
              setComprobando(false);
            } else {
              setComprobando(false);
              setReverso(null);
              setRevisando(false);
              setAviso('No pudimos leer el reverso. Acércalo al recuadro y evita reflejos.');
              rechazosDeReverso.current += 1;
            }
          });
      }
    },
    {
      // La cara es la CLAVE del lazo: cambiarla lo rearma. Sin esto, el reverso no captura.
      clave: `${cara}:${reverso === null ? 'sin' : 'con'}`,
      // La simetría que impide capturar el anverso dos veces.
      soloConRostroImpreso: cara === 'anverso',
      sinRostroImpreso: cara === 'reverso',
      // El rect real del recuadro: la detección mira EXACTAMENTE donde la persona pone el DNI.
      region: () => marcoRef.current?.getBoundingClientRect() ?? null,
    },
  );

  if (fallo !== null) {
    const m = mensajeDelFallo(fallo);
    return (
      <Pantalla>
        <Pasos paso={1} onVolver={onVolver} />
        <div className="flex flex-1 flex-col justify-center gap-3 px-6">
          <h1 className="text-[24px] font-bold leading-tight">{m.titulo}</h1>
          <p className="text-[14px] leading-relaxed" style={{ color: 'rgba(16,17,20,.6)' }}>
            {m.queHacer}
          </p>
          {/*
            No se ofrece reintentar cuando está denegado: en iOS `getUserMedia` ya no vuelve a
            preguntar nunca, así que el botón no haría nada. Un botón que no puede funcionar es
            peor que ningún botón — la persona lo pulsa hasta que se rinde.
          */}
          {fallo !== 'denegado' && fallo !== 'sin_https' ? (
            <div className="mt-3">
              <Boton onClick={reintentar}>Intentar otra vez</Boton>
            </div>
          ) : null}
        </div>
        <Firma />
      </Pantalla>
    );
  }

  // ── ESCANEO DNI EXITOSO: las dos caras, y la puerta de atrás ────────────────
  if (revisando && anverso !== null && reverso !== null) {
    return (
      <Pantalla>
        <Pasos paso={1} onVolver={onVolver} />
        <div className="flex flex-1 flex-col px-6 pt-6">
          <h1 className="text-center text-[24px] font-bold">Escanea tu DNI</h1>
          <p className="mt-1 text-center text-[13.5px]" style={{ color: 'rgba(16,17,20,.55)' }}>
            Documento capturado correctamente
          </p>

          <Miniatura etiqueta="(Anverso)" src={anverso} />
          <Miniatura etiqueta="(Reverso)" src={reverso} />
          {/* La comprobación del servidor corre AQUÍ, con las fotos ya a la vista (ver la
              cabecera): la persona sabe qué pasa, y Continuar espera a que termine. */}
          {comprobando ? (
            <p className="mt-2 text-center text-[12.5px]" style={{ color: 'rgba(16,17,20,.55)' }}>
              Comprobando el reverso…
            </p>
          ) : null}

          <div className="mt-auto flex flex-col gap-3 pb-2 pt-6">
            <Boton disabled={comprobando} onClick={() => onListo({ anverso, reverso })}>
              Continuar
            </Boton>
            {/*
              "Volver a capturar" no es cortesía: la persona ve sus fotos y sabe mejor que
              nuestro medidor de nitidez si su DNI se lee. Quitarlo la obligaría a mandar una
              foto que ella ya sabe que está mal, esperar el rechazo, y empezar de cero.
            */}
            <BotonSecundario
              onClick={() => {
                setAnverso(null);
                setReverso(null);
                setCara('anverso');
                setRevisando(false);
                rechazosDeReverso.current = 0;
              }}
            >
              Volver a capturar
            </BotonSecundario>
          </div>
        </div>
        <Firma />
      </Pantalla>
    );
  }

  // ── ESCANEO ANVERSO / REVERSO: la cámara ────────────────────────────────────
  return (
    <div
      className="relative mx-auto flex min-h-[100dvh] w-full max-w-[440px] flex-col overflow-hidden"
      style={{ background: '#101114', fontFamily: 'var(--tipografia, Inter, system-ui, sans-serif)' }}
    >
      <video
        ref={videoRef}
        playsInline
        muted
        autoPlay
        className="absolute inset-0 h-full w-full object-cover"
      />
      {/* Un velo para que el texto blanco se lea sobre cualquier fondo: una pared clara, el sol. */}
      <div className="absolute inset-0" style={{ background: 'rgba(16,17,20,.42)' }} aria-hidden />

      <div className="relative flex flex-1 flex-col">
        <Pasos paso={1} onVolver={onVolver} sobreVideo />

        <div className="px-6 pt-5 text-center text-white">
          <h1 className="text-[23px] font-bold">Escanea tu DNI</h1>
          <p className="mt-1 text-[13px]" style={{ color: 'rgba(255,255,255,.82)' }}>
            {cara === 'anverso'
              ? 'Coloca en frente la foto de tu DNI, en el recuadro.'
              : 'Ahora voltea tu DNI y coloca el reverso en el recuadro.'}
          </p>
        </div>

        <div className="px-6 pt-4">
          <span
            className="inline-block rounded-full px-3 py-1 text-[12.5px] font-semibold"
            style={{ background: 'rgba(255,255,255,.92)', color: '#101114' }}
          >
            {cara === 'anverso' ? 'Anverso' : '(Reverso)'}
          </span>
        </div>

        <div className="flex flex-1 items-center">
          <div className="w-full">
            <Marco progreso={captura.progreso} innerRef={marcoRef} />
          </div>
        </div>

        {/*
          Las tres pastillas del Figma. "DNI" dice la verdad de la cara que toca: en el
          anverso se enciende cuando la foto impresa está a la vista; en el reverso, cuando
          YA NO se ve (documento volteado). Es exactamente lo que el lazo exige para disparar.
        */}
        <div className="flex justify-center gap-2 pb-3">
          <Pastilla
            texto="DNI"
            ok={captura.rostroImpresoOk && captura.pista !== 'Apunta a tu DNI, no a tu rostro'}
          />
          <Pastilla texto="Luz" ok={captura.luzOk} />
          <Pastilla texto="Nitidez" ok={captura.nitidezOk} />
        </div>

        {/* Ya sin la rama de «Comprobando el reverso…»: esa espera vive ahora en la pantalla
            de éxito, donde la persona ve sus fotos — esta pantalla nunca se ve comprobando. */}
        <p
          className="pb-4 text-center text-[12.5px] font-medium"
          style={{ color: 'rgba(255,255,255,.9)', minHeight: '1.2em' }}
          aria-live="polite"
        >
          {captura.pista ?? aviso ?? 'Detección automática…'}
        </p>

        <div className="flex justify-center gap-3 pb-6">
          <Recuadro etiqueta="Anverso" hecho={anverso !== null} activo={cara === 'anverso'} />
          <Recuadro etiqueta="Reverso" hecho={reverso !== null} activo={cara === 'reverso'} />
        </div>
      </div>
    </div>
  );
}

function Miniatura({ etiqueta, src }: { etiqueta: string; src: string }) {
  return (
    <div className="mt-5">
      <p className="mb-1.5 text-center text-[12.5px] font-semibold" style={{ color: 'var(--acento, #3332f2)' }}>
        {etiqueta}
      </p>
      <img
        src={src}
        alt=""
        className="mx-auto w-[62%] rounded-xl"
        style={{ aspectRatio: '1.586', objectFit: 'cover', border: '1px solid rgba(16,17,20,.1)' }}
      />
    </div>
  );
}

/** Las dos tarjetas del Figma: la capturada lleva su palomita verde. */
function Recuadro({ etiqueta, hecho, activo }: { etiqueta: string; hecho: boolean; activo: boolean }) {
  return (
    <div
      className="grid h-[58px] w-[98px] place-items-center rounded-xl transition"
      style={{
        background: 'rgba(255,255,255,.14)',
        border: `1.5px solid ${activo ? 'var(--acento-2, #6b5cff)' : 'rgba(255,255,255,.22)'}`,
      }}
    >
      {hecho ? (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-label={`${etiqueta} listo`}>
          <path d="m5 12.5 4.5 4.5L19 7" stroke="#12A150" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        <span className="text-[11px] font-semibold" style={{ color: 'rgba(255,255,255,.75)' }}>
          {etiqueta}
        </span>
      )}
    </div>
  );
}
